import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { resetPassword, verifyResetToken, clearError, selectAuthLoading, selectAuthError } from '../../store/slices/authSlice';
import AuthLayout from './AuthLayout';
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  LoaderCircle,
  CheckCircle,
  XCircle,
  ArrowLeft,
  KeyRound,
  User,
  Clock,
  AlertTriangle
} from 'lucide-react';

const ResetPassword = ({ showToast }) => {
  const [formData, setFormData] = useState({
    password: '',
    password_confirmation: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [token, setToken] = useState('');
  const [email, setEmail] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [tokenError, setTokenError] = useState('');
  const [expiresIn, setExpiresIn] = useState(null);
  const [verificationChecked, setVerificationChecked] = useState(false);
  const [verifyMessage, setVerifyMessage] = useState('');
  
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const loading = useSelector(selectAuthLoading);
  const error = useSelector(selectAuthError);

  // Styles matching Register component
  const inputStyles = {
    width: '100%',
    padding: '16px 20px 16px 50px',
    fontSize: '17px',
    borderRadius: '10px',
    border: '2px solid #e2e8f0',
    background: '#ffffff',
    transition: 'all 0.2s ease',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    color: '#1a1a2e',
    outline: 'none',
    lineHeight: '1.5',
  };

  const labelStyles = {
    display: 'block',
    fontWeight: '600',
    marginBottom: '8px',
    color: '#1a1a2e',
    fontSize: '15px',
  };

  const inputWrapperStyles = {
    marginBottom: '20px',
    position: 'relative',
  };

  const iconWrapperStyles = {
    position: 'absolute',
    left: '18px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#94a3b8',
    pointerEvents: 'none',
    zIndex: 1,
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tokenParam = params.get('token');
    const emailParam = params.get('email');

    if (tokenParam && emailParam) {
      setToken(tokenParam);
      setEmail(emailParam);
      verifyToken(tokenParam, emailParam);
    } else {
      setIsVerifying(false);
      setTokenError('Missing token or email parameter');
    }

    return () => {
      dispatch(clearError());
    };
  }, [location, dispatch]);

  // Function to verify token validity
  const verifyToken = async (token, email) => {
    setIsVerifying(true);
    setTokenError('');
    setVerifyMessage('');
    
    try {
      // Call the verifyResetToken thunk
      const result = await dispatch(verifyResetToken({
        email,
        token,
        password: '', // Not needed for verification
        password_confirmation: '' // Not needed for verification
      })).unwrap();
      
      if (result.success) {
        setIsTokenValid(true);
        // Set expiry from response data if available
        if (result.data && result.data.expires_in) {
          setExpiresIn(result.data.expires_in);
        } else {
          // Default to 30 minutes
          setExpiresIn(30);
        }
        if (result.message) {
          setVerifyMessage(result.message);
        }
      } else {
        setIsTokenValid(false);
        setTokenError(result.message || 'Invalid or expired token');
      }
      
    } catch (err) {
      setIsTokenValid(false);
      
      // Check for specific error types
      if (err.message && err.message.toLowerCase().includes('expired')) {
        setTokenError('This reset link has expired. Please request a new one.');
      } else if (err.message && err.message.toLowerCase().includes('invalid')) {
        setTokenError('Invalid reset link. Please request a new one.');
      } else {
        setTokenError(err.message || 'Failed to verify token');
      }
      
      // If backend returned specific error data
      if (err.errors && typeof err.errors === 'object') {
        const errorMessages = Object.values(err.errors).flat().join(', ');
        if (errorMessages) {
          setTokenError(errorMessages);
        }
      }
    } finally {
      setIsVerifying(false);
      setVerificationChecked(true);
    }
  };

  // Countdown timer for expiry
  useEffect(() => {
    if (expiresIn && isTokenValid && !isSuccess) {
      const timer = setInterval(() => {
        setExpiresIn(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            setTokenError('Token has expired. Please request a new reset link.');
            setIsTokenValid(false);
            return 0;
          }
          return prev - 1;
        });
      }, 60000); // Update every minute
      
      return () => clearInterval(timer);
    }
  }, [expiresIn, isTokenValid, isSuccess]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFieldErrors({});

    // Check if token is expired
    if (expiresIn !== null && expiresIn <= 0) {
      setTokenError('Token has expired. Please request a new reset link.');
      return;
    }

    try {
      await dispatch(resetPassword({
        email,
        token,
        password: formData.password,
        password_confirmation: formData.password_confirmation
      })).unwrap();
      
      setIsSuccess(true);
      if (showToast) {
        showToast('✅ Password reset successful! Redirecting to login...', 'success');
      }
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      // Handle field errors from API
      if (err.errors) {
        setFieldErrors(err.errors);
      }
      
      // Check if error is about expired token
      if (err.message) {
        if (err.message.toLowerCase().includes('expired')) {
          setTokenError(err.message);
          setIsTokenValid(false);
        }
        if (showToast) {
          showToast(err.message, 'error');
        }
      }
    }
  };

  // Format time for display
  const formatTime = (minutes) => {
    if (minutes === null || minutes === undefined) return 'Unknown';
    if (minutes <= 0) return 'Expired';
    if (minutes < 60) {
      return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (remainingMinutes === 0) {
      return `${hours} hour${hours !== 1 ? 's' : ''}`;
    }
    return `${hours}h ${remainingMinutes}m`;
  };

  // Loading state
  if (isVerifying) {
    return (
      <AuthLayout title="Verifying Link" subtitle="Please wait while we verify your reset link">
        <div style={{ maxWidth: '520px', margin: '0 auto', textAlign: 'center', padding: '40px 0' }}>
          <LoaderCircle size={48} style={{ animation: 'spin 1s linear infinite', color: '#d4af37' }} />
          <p style={{ marginTop: '20px', color: '#64748b', fontSize: '16px' }}>
            Verifying your reset link...
          </p>
        </div>
      </AuthLayout>
    );
  }

  // Invalid token/email state
  if (!isTokenValid || !token || !email) {
    return (
      <AuthLayout title="Invalid Reset Link" subtitle="The password reset link is invalid or has expired">
        <div style={{ maxWidth: '520px', margin: '0 auto' }}>
          {tokenError && (
            <div style={{ 
              marginBottom: '24px', 
              padding: '16px 20px', 
              borderRadius: '10px',
              fontSize: '15px',
              backgroundColor: '#fee2e2',
              color: '#dc2626',
              border: '1px solid #fecaca',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <XCircle size={20} />
              <span>{tokenError}</span>
            </div>
          )}
          
          <div style={{ 
            marginBottom: '24px', 
            padding: '20px',
            borderRadius: '10px',
            backgroundColor: '#fef3c7',
            color: '#92400e',
            border: '1px solid #fde68a',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px'
          }}>
            <AlertTriangle size={24} style={{ flexShrink: 0, marginTop: '2px' }} />
            <div>
              <strong style={{ display: 'block', marginBottom: '4px' }}>Link Expired or Invalid</strong>
              <p style={{ margin: '0', fontSize: '14px' }}>
                Your password reset link is either invalid or has expired. 
                This usually happens if:
              </p>
              <ul style={{ marginTop: '8px', paddingLeft: '20px', fontSize: '14px' }}>
                <li>The link was already used to reset your password</li>
                <li>More than 30 minutes have passed since the request</li>
                <li>The link was copied incorrectly</li>
              </ul>
            </div>
          </div>
          
          <Link 
            to="/forgot-password" 
            style={{
              display: 'block',
              width: '100%',
              padding: '18px',
              fontSize: '18px',
              fontWeight: '700',
              borderRadius: '10px',
              backgroundColor: '#d4af37',
              color: '#1a1a2e',
              border: 'none',
              textAlign: 'center',
              textDecoration: 'none',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#c5a032'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#d4af37'}
          >
            Request New Reset Link
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Reset Password" subtitle="Enter your new password">
      <form onSubmit={handleSubmit} style={{ maxWidth: '520px', margin: '0 auto' }}>
        {/* Display Verify Success Message */}
        {verifyMessage && (
          <div style={{ 
            marginBottom: '24px', 
            padding: '16px 20px', 
            borderRadius: '10px',
            fontSize: '15px',
            backgroundColor: '#d1fae5',
            color: '#065f46',
            border: '1px solid #a7f3d0',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <CheckCircle size={20} />
            <span>{verifyMessage}</span>
          </div>
        )}

        {/* Display Error Message */}
        {error && !fieldErrors && !tokenError && (
          <div style={{ 
            marginBottom: '24px', 
            padding: '16px 20px', 
            borderRadius: '10px',
            fontSize: '15px',
            backgroundColor: '#fee2e2',
            color: '#dc2626',
            border: '1px solid #fecaca',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <XCircle size={20} />
            <span>{error}</span>
          </div>
        )}

        {/* Token Expiry Warning */}
        {expiresIn !== null && expiresIn <= 5 && expiresIn > 0 && (
          <div style={{ 
            marginBottom: '24px', 
            padding: '12px 16px', 
            borderRadius: '10px',
            fontSize: '14px',
            backgroundColor: '#fef3c7',
            color: '#92400e',
            border: '1px solid #fde68a',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <Clock size={18} />
            <span>
              <strong><AlertTriangle size={14} style={{ verticalAlign: '-2px' }} /> Link expiring soon!</strong> You have {formatTime(expiresIn)} remaining.
            </span>
          </div>
        )}

        {/* Display Success Message */}
        {isSuccess && (
          <div style={{ 
            marginBottom: '24px', 
            padding: '16px 20px', 
            borderRadius: '10px',
            fontSize: '15px',
            backgroundColor: '#d1fae5',
            color: '#065f46',
            border: '1px solid #a7f3d0',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <CheckCircle size={20} />
            <span>Password reset successful! Redirecting to login...</span>
          </div>
        )}

        {/* Email Field - Read Only */}
        <div style={inputWrapperStyles}>
          <label style={labelStyles}>Email Address</label>
          <div style={{ position: 'relative' }}>
            <span style={iconWrapperStyles}><Mail size={20} /></span>
            <input
              type="email"
              style={{
                ...inputStyles,
                backgroundColor: '#f1f5f9',
                cursor: 'not-allowed',
                opacity: 0.7,
                borderColor: '#e2e8f0'
              }}
              value={email}
              readOnly
              disabled
            />
          </div>
          <div style={{
            fontSize: '13px',
            color: '#94a3b8',
            marginTop: '6px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            <User size={14} />
            Password will be reset for this email address.
          </div>
        </div>

        {/* New Password Field */}
        <div style={inputWrapperStyles}>
          <label style={labelStyles}>New Password *</label>
          <div style={{ position: 'relative' }}>
            <span style={iconWrapperStyles}><Lock size={20} /></span>
            <input
              type={showPassword ? 'text' : 'password'}
              name="password"
              style={{
                ...inputStyles,
                paddingRight: '50px',
                borderColor: fieldErrors.password ? '#dc2626' : '#e2e8f0'
              }}
              placeholder="Min 8 characters"
              value={formData.password}
              onChange={handleChange}
              required
              minLength={8}
              disabled={isSuccess}
              onFocus={(e) => e.target.style.borderColor = '#d4af37'}
              onBlur={(e) => e.target.style.borderColor = fieldErrors.password ? '#dc2626' : '#e2e8f0'}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              style={{
                position: 'absolute',
                right: '16px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '6px',
                color: '#94a3b8',
                zIndex: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {showPassword ? <EyeOff size={22} /> : <Eye size={22} />}
            </button>
          </div>
          {fieldErrors.password && (
            <span style={{
              color: '#dc2626',
              fontSize: '14px',
              marginTop: '6px',
              display: 'block'
            }}>
              {fieldErrors.password}
            </span>
          )}
          <div style={{
            fontSize: '13px',
            color: '#94a3b8',
            marginTop: '6px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            <KeyRound size={14} />
            Password must be at least 8 characters long.
          </div>
        </div>

        {/* Confirm Password Field */}
        <div style={inputWrapperStyles}>
          <label style={labelStyles}>Confirm New Password *</label>
          <div style={{ position: 'relative' }}>
            <span style={iconWrapperStyles}><Lock size={20} /></span>
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              name="password_confirmation"
              style={{
                ...inputStyles,
                paddingRight: '50px',
                borderColor: fieldErrors.password_confirmation ? '#dc2626' : '#e2e8f0'
              }}
              placeholder="Confirm your new password"
              value={formData.password_confirmation}
              onChange={handleChange}
              required
              disabled={isSuccess}
              onFocus={(e) => e.target.style.borderColor = '#d4af37'}
              onBlur={(e) => e.target.style.borderColor = fieldErrors.password_confirmation ? '#dc2626' : '#e2e8f0'}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              style={{
                position: 'absolute',
                right: '16px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '6px',
                color: '#94a3b8',
                zIndex: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {showConfirmPassword ? <EyeOff size={22} /> : <Eye size={22} />}
            </button>
          </div>
          {fieldErrors.password_confirmation && (
            <span style={{
              color: '#dc2626',
              fontSize: '14px',
              marginTop: '6px',
              display: 'block'
            }}>
              {fieldErrors.password_confirmation}
            </span>
          )}
        </div>

        {/* Token Expiry Info Box */}
        <div style={{ 
          marginBottom: '24px', 
          padding: '14px 18px',
          backgroundColor: expiresIn !== null && expiresIn <= 5 ? '#fef3c7' : '#f8fafc',
          borderRadius: '8px',
          fontSize: '14px',
          color: expiresIn !== null && expiresIn <= 5 ? '#92400e' : '#64748b',
          border: `1px solid ${expiresIn !== null && expiresIn <= 5 ? '#fde68a' : '#e2e8f0'}`,
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          <Clock size={18} color={expiresIn !== null && expiresIn <= 5 ? '#92400e' : '#94a3b8'} />
          <span>
            <strong>Link expires in:</strong> {formatTime(expiresIn)}
            {expiresIn !== null && expiresIn <= 5 && expiresIn > 0 && (
              <span style={{ display: 'block', fontSize: '13px', marginTop: '4px' }}>
                <AlertTriangle size={14} style={{ verticalAlign: '-2px' }} /> Please complete your password reset before the link expires.
              </span>
            )}
            {expiresIn !== null && expiresIn > 5 && (
              <span style={{ display: 'block', fontSize: '13px', marginTop: '4px' }}>
                You have {formatTime(expiresIn)} to complete this password reset.
              </span>
            )}
          </span>
        </div>

        {/* Submit Button */}
        <button 
          type="submit" 
          disabled={loading || isSuccess || (expiresIn !== null && expiresIn <= 0)}
          style={{
            width: '100%',
            padding: '18px',
            fontSize: '18px',
            fontWeight: '700',
            borderRadius: '10px',
            backgroundColor: '#d4af37',
            color: '#1a1a2e',
            border: 'none',
            cursor: (loading || isSuccess || (expiresIn !== null && expiresIn <= 0)) ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s ease',
            opacity: (loading || isSuccess || (expiresIn !== null && expiresIn <= 0)) ? 0.7 : 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px'
          }}
          onMouseEnter={(e) => {
            if (!loading && !isSuccess && !(expiresIn !== null && expiresIn <= 0)) {
              e.target.style.backgroundColor = '#c5a032';
            }
          }}
          onMouseLeave={(e) => {
            if (!loading && !isSuccess && !(expiresIn !== null && expiresIn <= 0)) {
              e.target.style.backgroundColor = '#d4af37';
            }
          }}
        >
          {loading ? (
            <>
              <LoaderCircle size={22} style={{ animation: 'spin 1s linear infinite' }} />
              Resetting...
            </>
          ) : (expiresIn !== null && expiresIn <= 0) ? (
            <>
              <XCircle size={20} /> Link Expired
            </>
          ) : (
            <>
              <KeyRound size={20} /> Reset Password
            </>
          )}
        </button>

        {/* Back to Login Link */}
        <div style={{ 
          textAlign: 'center', 
          marginTop: '24px'
        }}>
          <Link to="/login" style={{ 
            color: '#64748b',
            textDecoration: 'none',
            fontSize: '15px',
            fontWeight: '500',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'color 0.2s ease'
          }}
          onMouseEnter={(e) => e.target.style.color = '#d4af37'}
          onMouseLeave={(e) => e.target.style.color = '#64748b'}
          >
            <ArrowLeft size={18} /> Back to Sign In
          </Link>
        </div>
      </form>

      {/* Add spin animation */}
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </AuthLayout>
  );
};

export default ResetPassword;